import { z } from 'zod';
import { colourSchema } from './classification';
/**
 * Income categories - the mirror of `expense_categories`, and the real gap the
 * dashboard exposed.
 *
 * Fint has classified spend since 0001 and has never classified revenue.
 * `items` is a catalogue, not a classification; a bank deposit has no lines at
 * all. So this list, deliberately built to the same shape as the expense side
 * so the two report paths stay symmetrical.
 */
/**
 * The income categories a new org starts with.
 *
 * Generic on purpose. The owner's spreadsheet calls these `REV-Dev`,
 * `REV-Video` and `REV-Other`, but the `REV-` prefix is a namespace for a
 * single flat list that mixed income and expense together - give somebody two
 * lists and the prefix has no job. Renaming these is a first-run act; being
 * handed somebody else's vocabulary is not.
 *
 * `Reimbursed expenses` earns its place: it is the income side of a billable
 * expense recharge, and having it seeded stops people booking recharges as
 * `Services` and overstating what the business actually sells.
 */
export const DEFAULT_INCOME_CATEGORIES = [
    'Services',
    'Products',
    'Consulting',
    'Subscriptions and retainers',
    'Reimbursed expenses',
    'Interest and other income',
    'Other income',
];
/**
 * Seeded on BOTH sides of every book, flagged `interBook`: the ways a person
 * pays themselves out of a business they own. An LLP pays its partner
 * remuneration; a company pays its owner a salary. Each book records its half
 * under the same name, and the Overview eliminates the pair from its totals.
 *
 * "Salaries and wages" is deliberately not here - that is staff, and a real
 * cost the Overview must keep.
 */
export const INTER_BOOK_CATEGORIES = {
    income: ['Partner remuneration', 'Salary'],
    expense: ['Partner remuneration', "Owner's salary"],
};
/**
 * The emoji each seeded income category is born with, by name - the income
 * half of the table migration 0015 stamped onto existing orgs. See
 * `DEFAULT_EXPENSE_CATEGORY_EMOJI` for why it is keyed by name and why the
 * migration, not this file, is the source of truth.
 */
export const DEFAULT_INCOME_CATEGORY_EMOJI = {
    Services: '🛠️',
    Products: '📦',
    Consulting: '🧠',
    'Subscriptions and retainers': '🔁',
    'Reimbursed expenses': '↩️',
    'Interest and other income': '📈',
    'Other income': '💰',
};
/**
 * The inter-book pair's emoji, on both sides. The same picture for the same
 * money: a handshake for partner remuneration and a briefcase for a salary,
 * whichever book is looking at it.
 */
export const INTER_BOOK_CATEGORY_EMOJI = {
    income: { 'Partner remuneration': '🤝', Salary: '💼' },
    expense: { 'Partner remuneration': '🤝', "Owner's salary": '💼' },
};
const categoryFields = {
    name: z.string().trim().min(1, 'Give the category a name').max(120),
    description: z.string().trim().max(500).optional(),
    colour: colourSchema.optional(),
    /**
     * One level only. A parent must itself have no parent - arbitrary depth needs
     * a recursive CTE for every total, and the portability rules in schema.ts
     * steer away from those. One level covers every chart of accounts a business
     * this size has, and the alternative is users encoding hierarchy in names
     * ("Admin - Rent", "Admin - Utilities") and then wanting it parsed, which is
     * a data repair rather than a column.
     */
    parentId: z.string().trim().max(36).nullable().optional(),
    /**
     * Whether money in this category is left out of the headline totals.
     *
     * A property of the CATEGORY and not of the record: "Savings is not an
     * expense" is a policy stated once, and a per-record flag is one chance to
     * forget per record. Writing it needs `settings.manage`, because it changes
     * what every headline figure means - that is an administrator's decision, not
     * a data-entry one.
     */
    excludedFromTotals: z.boolean().optional(),
    /** Money between this person's own books. See migration 0014. */
    interBook: z.boolean().optional(),
    /**
     * One emoji, for the quick-add tiles. Up to 16 characters (code points, as
     * the varchar(16) column counts them) so a ZWJ sequence or a skin-tone
     * modifier fits; empty string clears it.
     */
    emoji: z.string().trim().max(16).nullable().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    isActive: z.boolean().optional(),
};
export const createIncomeCategorySchema = z.object(categoryFields).strict();
export const updateIncomeCategorySchema = z
    .object({ ...categoryFields, name: categoryFields.name.optional() })
    .strict();
/**
 * The four columns migration 0010 added to `expense_categories`, editable.
 *
 * A separate schema from `createExpenseCategorySchema` rather than a `.partial()`
 * of it, because the two are gated differently: anyone with `expense.create`
 * may add a category while filing a bill, but only `settings.manage` may
 * recolour, reparent or - the one that matters - exclude one from the totals.
 */
export const updateExpenseCategorySchema = z
    .object({ ...categoryFields, name: categoryFields.name.optional() })
    .strict();
/** Which list is being asked for. An allow-list; there is no "both" shortcut. */
export const CATEGORY_KINDS = ['expense', 'income'];
export const listCategoriesQuerySchema = z
    .object({
    includeInactive: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .default(false),
})
    .strict();
/**
 * Reorder a whole list in one call.
 *
 * The order it sets is the order of every picker and every breakdown legend, so
 * the user controls what the dashboard looks like. Sending the full id list
 * rather than a pair of positions makes the write idempotent and leaves no
 * intermediate state where two rows share a `sortOrder`.
 */
export const reorderCategoriesSchema = z
    .object({
    ids: z.array(z.string().trim().min(1).max(36)).min(1).max(500),
})
    .strict();
/** Say what kind of income a bank deposit was. `null` un-says it. */
export const setTransactionIncomeCategorySchema = z
    .object({
    incomeCategoryId: z.string().trim().max(36).nullable(),
})
    .strict();
//# sourceMappingURL=categories.js.map